import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { AppContextType, UserRole, Hackday, UserProfile, RoleToggleState } from '../types/context';
import { useApiClient } from '../services/api';
import { getPendingRegistration, clearPendingRegistration } from '../utils/pendingRegistration';

// Create the context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Custom hook to use the app context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Props for the AppProvider component
interface AppProviderProps {
  children: React.ReactNode;
}

// AppProvider component that wraps the application
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user: clerkUser, isLoaded, isSignedIn } = useUser();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [hackdays, setHackdays] = useState<Hackday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roleToggleState, setRoleToggleState] = useState<RoleToggleState>({
    isToggling: false,
    error: null,
  });
  const [userRole, setUserRole] = useState<UserRole | null>(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('user.role') : null;
    return (stored === 'sponsor' || stored === 'participant') ? stored : null;
  });
  const apiClient = useApiClient();
  const hasInitialized = useRef(false);

  // Convert Clerk user to our UserProfile format
  const createUserProfile = (clerkUser: any, backendUser?: any): UserProfile | null => {
    if (!clerkUser) return null;

    // Get role only from backend - localStorage is managed separately in initialization
    const backendRole = backendUser?.roles?.[0] as UserRole | undefined;

    return {
      id: clerkUser.id,
      clerkId: clerkUser.id,
      email: clerkUser.emailAddresses?.[0]?.emailAddress || '',
      username: clerkUser.username || undefined,
      role: backendRole,
      createdAt: new Date(clerkUser.createdAt),
      updatedAt: new Date(clerkUser.updatedAt),
    };
  };

  // Refresh user profile from backend API
  const refreshUser = useCallback(async (): Promise<void> => {
    if (isSignedIn && clerkUser) {
      try {
        // Fetch user profile from backend API to get Notion ID and role
        const response = await apiClientRef.current.getCurrentUser();

        if (response.success && response.data) {
          // Backend provides the complete user profile with Notion ID and roles
          const backendData = response.data as any;
          const profile = createUserProfile(clerkUser, backendData);
          setUserProfile(profile);

          // Sync role from backend to local state if available
          // REMOVED: We trust client state for role to avoid race conditions
          /*
          if (backendData.roles && backendData.roles.length > 0) {
            const backendRole = backendData.roles[0];
            if (backendRole === 'sponsor' || backendRole === 'participant') {
              setUserRole(backendRole);
              localStorage.setItem('user.role', backendRole);
            }
          }
          */
        } else {
          // Fallback to Clerk user data if API fails
          const profile = createUserProfile(clerkUser);
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Error fetching user profile from API:', error);
        // Fallback to Clerk user data
        const profile = createUserProfile(clerkUser);
        setUserProfile(profile);
      }
    } else {
      setUserProfile(null);
    }
  }, [isSignedIn, clerkUser]);

  // Fetch hackdays from API
  const refreshHackdays = useCallback(async (): Promise<void> => {
    try {
      const response = await apiClientRef.current.getHackdays();

      if (response.success && response.data) {
        // Support both legacy array responses and new { hackdays } envelope
        const rawHackdays: any[] = Array.isArray(response.data)
          ? response.data
          : response.data.hackdays || [];

        // Convert date strings back to Date objects
        const hackdaysWithDates = rawHackdays.map((hackday: any) => ({
          ...hackday,
          startDate: new Date(hackday.startDate),
          endDate: new Date(hackday.endDate),
          registrationDeadline: new Date(hackday.registrationDeadline),
          createdAt: new Date(hackday.createdAt),
          updatedAt: new Date(hackday.updatedAt),
          // Compute currentParticipants from participants array (new backend structure)
          currentParticipants: hackday.participants?.length || hackday.currentParticipants || 0,
        }));

        setHackdays(hackdaysWithDates);
      } else {
        console.error('Failed to fetch hackdays:', response.error);
        // Fallback to empty array instead of mock data
        setHackdays([]);
      }
    } catch (error) {
      console.error('Error fetching hackdays:', error);
      setHackdays([]);
    }
  }, []);

  // Process pending registration after authentication
  const processPendingRegistration = useCallback(async () => {
    const pending = getPendingRegistration();
    if (!pending) return;

    console.log('[AppContext] Processing pending registration:', pending);

    try {
      // Set role directly without using toggleUserRole (to avoid circular dependency)
      localStorage.setItem('user.role', pending.role);
      setUserRole(pending.role);

      // Update backend with role
      const updateResponse = await apiClientRef.current.updateCurrentUser({ role: pending.role });
      if (!updateResponse.success) {
        console.warn('[AppContext] Failed to update role in backend:', updateResponse.error);
      }

      // Register for event
      const registrationData = {
        teamName: '', // Will be set in team registration modal
        eventId: pending.eventId,
        registrationType: 'solo' as const,
        lookingForTeam: true,
        lookingForSkills: [],
        teamMemberEmails: [],
      };

      const response = await apiClientRef.current.createTeamRegistration(registrationData);

      if (response.success) {
        console.log('[AppContext] Pending registration completed successfully');
        clearPendingRegistration();

        // Navigate to /events to show authenticated view
        setTimeout(() => {
          navigate('/events');
        }, 500);
      } else {
        console.error('[AppContext] Failed to complete pending registration:', response.error);
        clearPendingRegistration();
      }
    } catch (error) {
      console.error('[AppContext] Error processing pending registration:', error);
      clearPendingRegistration();
    }
  }, [navigate]);

  // Stable ref for API client to avoid re-rendering issues
  const apiClientRef = React.useRef(apiClient);
  apiClientRef.current = apiClient;

  // Effect to handle user authentication state changes
  useEffect(() => {
    const initializeApp = async () => {
      if (isLoaded) {
        // Only show loading spinner on initial mount, not on subsequent updates
        if (!hasInitialized.current) {
          setIsLoading(true);
        }

        // Refresh user profile from API
        if (isSignedIn && clerkUser) {
          try {
            const response = await apiClientRef.current.getCurrentUser();

            if (response.success && response.data) {
              const backendData = response.data as any;
              const profile = createUserProfile(clerkUser, backendData);
              setUserProfile(profile);

              console.log('[AppContext] User initialization:', {
                backendRoles: backendData.roles,
                profileRole: profile?.role,
                hasRoles: backendData.roles && backendData.roles.length > 0
              });

              // Backend has no roles - check for pending registration
              console.log('[AppContext] No backend roles found - checking for pending registration');
              const pending = getPendingRegistration();
              if (pending) {
                // Process pending registration
                await processPendingRegistration();
              }
              // Don't clear localStorage role if it exists - trust the client state
              // This allows the role to persist even if backend doesn't return it immediately
            } else {
              const profile = createUserProfile(clerkUser);
              setUserProfile(profile);
            }
          } catch (error) {
            console.error('Error fetching user profile from API:', error);
            const profile = createUserProfile(clerkUser);
            setUserProfile(profile);
          }
        } else {
          setUserProfile(null);
        }

        // Fetch hackdays
        try {
          const response = await apiClientRef.current.getHackdays();

          if (response.success && response.data) {
            // Backend historically returned an array (data = Hackday[])
            // but the client expects { hackdays: Hackday[], pagination: ... }.
            // Handle both shapes defensively to avoid runtime errors.
            const rawHackdays: any[] = Array.isArray(response.data)
              ? response.data
              : response.data.hackdays || [];

            const hackdaysWithDates = rawHackdays.map((hackday: any) => ({
              ...hackday,
              startDate: new Date(hackday.startDate),
              endDate: new Date(hackday.endDate),
              registrationDeadline: new Date(hackday.registrationDeadline),
              createdAt: new Date(hackday.createdAt),
              updatedAt: new Date(hackday.updatedAt),
              // Compute currentParticipants from participants array (new backend structure)
              currentParticipants: hackday.participants?.length || hackday.currentParticipants || 0,
            }));

            setHackdays(hackdaysWithDates);
          } else {
            console.error('Failed to fetch hackdays:', response.error);
            setHackdays([]);
          }
        } catch (error) {
          console.error('Error fetching hackdays:', error);
          setHackdays([]);
        }

        // Mark initialization as complete and hide loading spinner
        if (!hasInitialized.current) {
          hasInitialized.current = true;
          setIsLoading(false);
        }
      }
    };

    initializeApp();
    // Use clerkUser?.id instead of clerkUser to prevent re-runs when object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, clerkUser?.id]);

  // Toggle user role function - persists to backend and local state
  const toggleUserRole = useCallback(async (newRole: UserRole): Promise<void> => {
    if (!clerkUser) {
      throw new Error('User not authenticated');
    }

    setRoleToggleState({ isToggling: true, error: null });

    try {
      // Persist to localStorage immediately for instant UI updates
      localStorage.setItem('user.role', newRole);
      setUserRole(newRole);

      // Update user profile with role immediately
      if (userProfile) {
        setUserProfile({ ...userProfile, role: newRole });
      }

      // Update backend with role (async, don't block UI)
      const response = await apiClientRef.current.updateCurrentUser({ role: newRole });

      if (!response.success) {
        console.warn('Failed to update role in backend:', response.error);
        // Don't throw error - role is already set in localStorage
      }

      // Invalidate all user-related caches to ensure fresh data on next fetch
      apiClientRef.current.invalidateCache('users');
      apiClientRef.current.invalidateCache('currentattendees');
      apiClientRef.current.invalidateCache('currentsponsors');
      apiClientRef.current.invalidateCache('teammanagement');

      setRoleToggleState({ isToggling: false, error: null });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update role';
      console.error('Role toggle error:', errorMessage);
      setRoleToggleState({ isToggling: false, error: errorMessage });
      // Don't throw - localStorage role is set
    }
  }, [clerkUser, userProfile]);

  // Context value
  const value: AppContextType = {
    user: userProfile,
    isLoading: !isLoaded || isLoading,
    hackdays,
    userRole,
    roleToggleState,
    refreshUser,
    refreshHackdays,
    toggleUserRole,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
